export default class App {
	constructor() {
		this._resizeFunctionsArr = [];
		this._resizeFunctions = {};
		this._resizeFunctionsCounter = 0;
		this._resizeTimeout = null;
		this.resizeThrottle = false;

		this._scrollFunctionsArr = [];
		this._scrollFunctions = {};
		this._scrollFunctionsCounter = 0;
		this._scrollTimeout = null;
		this.scrollThrottle = false;

		this._resizeAnimationPreventerTimeout = 200;

		this.breakpoints = {
			xs: {
				gutter: 10,
				size: 0,
			},
			sm: {
				gutter: 20,
				size: 490,
			},
			md: {
				gutter: 30,
				size: 801,
			},
			lg: {
				gutter: 30,
				size: 1024,
			},
			xl: {
				gutter: 30,
				size: 1440,
			},
		}
	}

	init() {
		this.detectBrowser();
		this.setEventListeners();
		this.fullHeightListener();
		this.resizeAnimationPreventer();
		this.addNewWindowTitles();
		this.addNoOpener();
		this.addAnchorEnhancement();
		this.initSelectric();
		this.initTippy();
		this.initCopy();
		this.initScrollTo();
		this.initScrollToTop();
		this.initPrintListener();
		this.initLabelClick();
		this.initFormReordering();
		this.initProcessPrevNext();
		this.initProcessTabs();
		this.initProcessFixedHeight();
		this.initTreatmentteamPopup()

		return true;
	}

	setEventListeners() {
		window.addEventListener('resize', this.resize.bind(this), {
			passive: true
		});
		document.addEventListener('scroll', this.scroll.bind(this), {
			passive: true
		});

		document.querySelectorAll('main form label').forEach(label => {
			label.addEventListener('keydown', e => {
				if (e.key.toLowerCase().substr(0, 3) == 'ent') {
					label.click();
				}
			});
		});

		Array.prototype.slice.call(document.querySelectorAll('a')).filter(a => !a.hasAttribute('href')).forEach(a => {
			if (!a.hasAttribute('tabindex')) {
				a.setAttribute('tabindex', '0');
			}

			a.addEventListener('keydown', e => {
				if (e.key.toLowerCase().substr(0, 3) == 'ent') {
					a.click();
				}
			});
		});
	}

	resize() {
		const runFunctions = () => {
			for (let i = 0, length = this._resizeFunctionsArr.length; i < length; i++) {
				this._resizeFunctionsArr[i]();
			}
		};
		if (this._resizeTimeout !== null) {
			return;
		}
		runFunctions();
		if (this.resizeThrottle) {
			this._resizeTimeout = setTimeout(() => {
				this._resizeTimeout = null;
				runFunctions();
			}, this.resizeThrottle);
		}
	}

	/**
	 * @param {function} func Function to be called on a resize event
	 */
	registerResizeEvent(func) {
		this._resizeFunctions[this._resizeFunctionsCounter] = func;
		this._resizeFunctionsCounter = this._resizeFunctionsCounter + 1;

		// Rebuild array of events
		this._resizeFunctionsArr = [];
		Object.keys(this._resizeFunctions).forEach(key => {
			this._resizeFunctionsArr.push(this._resizeFunctions[key]);
		});

		return this._resizeFunctionsCounter - 1;
	}

	/**
	 * @param {Number} func Function's ID to be removed from the resize functions
	 */
	unregisterResizeEvent(func) {
		if (this._resizeFunctions[func]) {
			// Found
			delete this._resizeFunctions[func];

			// Rebuild array of events
			this._resizeFunctionsArr = [];
			Object.keys(this._resizeFunctions).forEach(key => {
				this._resizeFunctionsArr.push(this._resizeFunctions[key]);
			});

			return true;
		} else {
			// Not found
			return false;
		}
	}

	/**
	 * @description Fires all registered events for a scroll event in the document
	 */
	scroll() {
		const runFunctions = () => {
			for (let i = 0, length = this._scrollFunctionsArr.length; i < length; i++) {
				this._scrollFunctionsArr[i]();
			}
		};
		if (this._scrollTimeout !== null) {
			return;
		}
		runFunctions();
		if (this.scrollThrottle) {
			this._scrollTimeout = setTimeout(() => {
				this._scrollTimeout = null;
				runFunctions();
			}, this.scrollThrottle);
		}
	}

	/**
	 * @param {function} func Function to be called on a scroll event
	 */
	registerScrollEvent(func) {
		this._scrollFunctions[this._scrollFunctionsCounter] = func;
		this._scrollFunctionsCounter = this._scrollFunctionsCounter + 1;

		// Rebuild array of events
		this._scrollFunctionsArr = [];
		Object.keys(this._scrollFunctions).forEach(key => {
			this._scrollFunctionsArr.push(this._scrollFunctions[key]);
		});

		return this._scrollFunctionsCounter - 1;
	}

	/**
	 * @param {Number} func Function's ID to be removed from the scroll functions
	 */
	unregisterScrollEvent(func) {
		if (this._scrollFunctions[func]) {
			// Found
			delete this._scrollFunctions[func];

			// Rebuild array of events
			this._scrollFunctionsArr = [];
			Object.keys(this._scrollFunctions).forEach(key => {
				this._scrollFunctionsArr.push(this._scrollFunctions[key]);
			});

			return true;
		} else {
			// Not found
			return false;
		}
	}

	/**
	 * @description Adds a 'preventAnimations' class to the html tag on resizing
	 */
	resizeAnimationPreventer() {
		this.resizeTimer = null;
		this.windowWidth = window.innerWidth;
		this.registerResizeEvent(() => {
			if (window.innerWidth !== this.windowWidth) {
				document.documentElement.classList.add('preventAnimations');
				clearTimeout(this.resizeTimer);

				this.resizeTimer = setTimeout(() => {
					document.documentElement.classList.remove('preventAnimations');
				}, this._resizeAnimationPreventerTimeout);

				this.windowWidth = window.innerWidth;
			}
		});
	}

	/**
	 * @description Adds a CSS variable '--fullHeight' to the html tag that creates an 'accurate 100vh'
	 */
	fullHeightListener() {
		const fullHeight = () => {
			document.documentElement.style.setProperty('--fullHeight', `${window.innerHeight}px`);
		};
		this.registerResizeEvent(fullHeight);
		fullHeight();
	}

	/**
	 * @description Adds a browser-specific class to the html tag
	 */
	detectBrowser() {
		// Opera 8.0+
		let isOpera = (!!window.opr && !!opr.addons) || !!window.opera || navigator.userAgent.indexOf(' OPR/') >= 0;

		// Firefox 1.0+
		let isFirefox = typeof InstallTrigger !== 'undefined';

		let isIphone = window.navigator.userAgent.match(/iPhone/i);

		let isIpad = window.navigator.userAgent.match(/iPad/i);

		let isSafari = window.navigator.userAgent.match(/Safari/i) && (window.navigator.userAgent.match(/iPhone/i) || window.navigator.userAgent.match(/iPad/i) || window.navigator.userAgent.match(/macOS/i));

		// Internet Explorer 6-11
		let isIE = /*@cc_on!@*/ false || !!document.documentMode;

		// Edge 20+
		let isEdge = !isIE && !!window.StyleMedia;

		// Chrome 1 - 71
		let isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

		// Blink engine detection
		let isBlink = (isChrome || isOpera) && !!window.CSS;

		if (isOpera) {
			document.documentElement.classList.add('opera');
		}

		if (isFirefox) {
			document.documentElement.classList.add('firefox');
		}

		if (isSafari) {
			document.documentElement.classList.add('safari');
		}

		if (isIphone) {
			document.documentElement.classList.add('iphone');
		}

		if (isIpad) {
			document.documentElement.classList.add('ipad');
		}

		if (isIE) {
			document.documentElement.classList.add('ie');
		}

		if (isEdge) {
			document.documentElement.classList.add('edge');
		}

		if (isChrome) {
			document.documentElement.classList.add('chrome');
		}

		if (isBlink) {
			document.documentElement.classList.add('blink');
		}
	}

	/**
	 * @description Appends text to a tags that tells the user it'll open in a new tab/window. Add a tag with `data-new-window-text` property to change the appended text
	 */
	addNewWindowTitles() {
		let text = '(opent in een nieuw scherm)';
		if (document.querySelector('[data-new-window-text]')) {
			text = document.querySelector('[data-new-window-text]').dataset.newWindowText;
		}

		document.querySelectorAll('a[target="_blank"]').forEach(a => {
			a.setAttribute('title', `${a.getAttribute('title')} ${text}`.trim().replace(/  /g, " "));
		});
	}

	/**
	 * @description Adds the attribute 'rel' set to 'noopener' on external links; This is for security reasons/best practices
	 */
	addNoOpener() {
		document.querySelectorAll('a').forEach(a => {
			if (a.href && !a.hasAttribute('rel')) {
				if (a.hostname != location.hostname) {
					a.setAttribute('rel', 'noopener');
				}
			}
		});
	}

	/**
	 * @description Makes sure anchored tags get a class for it, so offsetting scroll is possible. Also edit anchor links to get the [data-scroll-to] attribute
	 */
	addAnchorEnhancement() {
		// Enhance anchors
		Array.prototype.slice.call(document.querySelectorAll('a[id]')).filter(a => !a.href).forEach(a => {
			// Get parent element, add 'anchored' class
			if (a.parentElement) {
				a.parentElement.classList.add('anchored');
			}
		});

		// Enhance anchor links
		Array.prototype.slice.call(document.querySelectorAll('a[href]')).filter(a => a.hash && a.hash != '' && a.getAttribute('href').substr(0, 1) == '#').forEach(a => {
			a.dataset.scrollTo = a.hash;
		});
	}

	/**
	 * @description Scroll element into view
	 */
	initScrollTo() {
		document.querySelectorAll('[data-scroll-to]').forEach(el => {
			el.addEventListener('click', e => {
				const target = document.querySelector(el.dataset.scrollTo);

				if (target && target.scrollIntoView) {
					e.preventDefault();
					target.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
						inline: 'start',
					});
				}
			});
		});
	}

	initScrollToTop() {
		this.scrollToTopContainer = document.querySelector('.scrollToTop');

		if (this.scrollToTopContainer) {
			const button = this.scrollToTopContainer.querySelector('button');
			const scroll = () => {
				if (document.documentElement.scrollTop > 400) {
					this.scrollToTopContainer.classList.add('active');
				} else {
					this.scrollToTopContainer.classList.remove('active');
				}
			};

			this.registerScrollEvent(scroll.bind(this));

			button.addEventListener('click', undefined => {
				this.scrollToTopContainer.classList.add('moving');
				button.blur();
				window.scrollTo({
					top: 0,
					behavior: 'smooth',
				});
				setTimeout(() => this.scrollToTopContainer.classList.remove('moving'), 1500);
			});
		}
	}

	initSelectric() {
		if (document.querySelectorAll('main select').length) {
			try {
				import( /* webpackChunkName: "modules/dropdown" */ 'selectric').then(e => {

					document.querySelectorAll('main select').forEach(select => {
						$(select).selectric({
							arrowButtonMarkup: `<b class="button"><svg class="sprite icon_chevron_down"><use xlink:href="#icon_chevron_down"></use></svg></b>`
						});
					});
				});
			} catch (e) {
				console.error(e);
			}
		}
	}

	/**
	 * @description Tooltip library
	 */
	initTippy() {
		if (document.querySelectorAll('[data-tippy-content]').length || document.querySelectorAll('[data-tippy-source]').length) {
			try {
				import( /* webpackChunkName: "modules/tooltip" */ 'tippy.js').then(e => {
					const tippy = e.default;
					tippy('[data-tippy-content]');

					if (document.querySelectorAll('[data-tippy-source]').forEach(source => {
							const target = document.querySelector(`[data-tippy-target="${source.dataset.tippySource}"]`);
							if (target) {
								tippy(source, {
									allowHTML: true,
									content: target.innerHTML,
									interactive: target.querySelectorAll('a, button').length > 0,
									trigger: 'focus',
								});
							}
						}));
				});
			} catch (e) {
				console.error(e);
			}
		}

	}

	/**
	 * @description Makes tags with the data-copy property clickable, copies its content to the clipboard
	 */
	initCopy() {
		document.querySelectorAll('[data-copy]').forEach(el => {
			el.addEventListener('click', () => {
				try {
					const copyContent = el.dataset.copy;
					const input = document.createElement('input');

					input.type = 'text';
					input.style.position = 'absolute';
					input.style.height = 0;
					input.style.overflow = 'hidden';
					input.value = copyContent;

					document.body.appendChild(input);
					input.select();
					document.execCommand('copy');
					input.parentNode.removeChild(input);
				} catch (e) {
					this.toast('Something unexpected went wrong', 'error');
					console.error(e);
				}
			});
		});
	}

	initPrintListener() {
		this.printTexts = document.querySelectorAll('[data-print]');

		if (this.printTexts.length) {
			let printElements = [];

			window.addEventListener('beforeprint', e => {
				this.printTexts.forEach(printParent => {
					let span = document.createElement('span');
					span.innerHTML = printParent.dataset.print;
					span.classList.add('printText');

					printElements.push(span);

					printParent.appendChild(span);
				});
			});

			window.addEventListener('afterprint', e => {
				printElements.forEach(printElement => {
					printElement.remove();
				});

				printElements = [];
			});
		}
	}

	initLabelClick() {
		document.querySelectorAll('label[for]').forEach(label => {
			label.addEventListener('keydown', e => {

				switch (e.key.toLowerCase()) {
					case ' ':
						e.preventDefault();
					case 'space':
					case 'enter':
						label.click();
						break;
				}
			});
		});
	}

	getCurrentBreakpoint() {
		const width = window.innerWidth;
		const breakpoints = Object.keys(this.breakpoints).reverse();

		let currentBreakpoint = breakpoints[breakpoints.length - 1];

		breakpoints.forEach(breakpoint => {
			if (width <= this.breakpoints[breakpoint].size) {
				currentBreakpoint = breakpoint;
			}
		});

		return currentBreakpoint;
	}

	initFormReordering() {
		document.querySelectorAll('.umbraco-forms-form .row.multiplechoice').forEach(row => {
			if (row.querySelectorAll('.checkboxList .checkbox').length > 6) {
				const list = row.querySelector('.checkboxList');
				const items = list.querySelectorAll('.checkbox');

				/* // Split in 3 arrays
				const n = 3;
				const groups = [[], [], []];

				const itemsPerColumn = Math.ceil(items.length / 3);

				for (let column = 0; column < n; column++) {
					for (let i = 0; i < itemsPerColumn; i++) {
						const value = items[i + column * itemsPerColumn];
						if (!value) {
							continue;
						}

						groups[column].push(value);
					}
				}
				
				list.classList.add('row');
				// Create column element for group
				groups.forEach(group => {
					const column = document.createElement('div');
					column.setAttribute('class', 'col-md-4');

					list.appendChild(column);
					group.forEach(item => {
						column.appendChild(item);
					});
				}); */
				// SIKE
				// We gonna put all of 'm in seperate rows

				items.forEach(item => {
					let column = document.createElement('div');
					column.classList.add('col-md-6');
					column.classList.add('col-lg-4');
					list.appendChild(column);
					column.appendChild(item);
				});

				list.classList.add('row');

				// Make sure they get full width
				row.querySelector('.labelColumn').setAttribute('class', 'col-12 labelColumn');
				row.querySelector('.fieldColumn').setAttribute('class', 'col-12 fieldColumn');

				// If tooltip's set, place it as normal text
				if (row.querySelector('.labelColumn .umbraco-forms-tooltip')) {
					const text = row.querySelector('.labelColumn .umbraco-forms-tooltip').dataset.tippyContent;
					const span = document.createElement('span');
					span.innerHTML = text;
					span.classList.add('helpText');
					row.querySelector('.labelColumn').appendChild(span);
					row.querySelector('.labelColumn .umbraco-forms-tooltip').remove();

					row.classList.add('columnized');
				}
			}
		});
	}

	initProcessPrevNext() {
		if (document.querySelectorAll('.stepTabs').length) {
			$(".stepContentContainer > div").each(function (e) {
				if (e != 0)
					$(this).removeClass("active");
			});

			$("#next").click(function () {
				if ($(".stepContentContainer > div.active").next().length != 0) {
					const nextElement = document.querySelector('.stepContentContainer > div.active').nextElementSibling;
					const stepIndex = nextElement.dataset.step;
					const selectedTab = document.querySelector('.stepTabs a[data-tab="' + stepIndex + '"]');
					const tab = document.querySelectorAll('.stepTabs a');

					tab.forEach(activeTab => {
						activeTab.classList.remove('active');
					})
					selectedTab.classList.add('active');

					$(".stepContentContainer > div.active").next().addClass("active");
					$(".stepContentContainer > div.active").prev().removeClass("active");
				} else {
					$(".stepContentContainer > div.active").removeClass("active");

					const selectedTab = document.querySelector('.stepTabs a[data-tab="1"]');
					const tab = document.querySelectorAll('.stepTabs a');

					tab.forEach(activeTab => {
						activeTab.classList.remove('active');
					})

					selectedTab.classList.add('active');
					$(".stepContentContainer > div:first").addClass("active");
				}
				window.slimmage.cr();
				return false;
			});

			$("#prev").click(function () {
				if ($(".stepContentContainer > div.active").prev().length != 0) {
					const previousElement = document.querySelector('.stepContentContainer > div.active').previousElementSibling;
					const stepIndex = previousElement.dataset.step;
					const selectedTab = document.querySelector('.stepTabs a[data-tab="' + stepIndex + '"]');
					const tab = document.querySelectorAll('.stepTabs a');

					tab.forEach(activeTab => {
						activeTab.classList.remove('active');
					})
					selectedTab.classList.add('active');

					$(".stepContentContainer > div.active").prev().addClass("active");
					$(".stepContentContainer > div.active").next().removeClass("active");
				} else {
					$(".stepContentContainer > div.active").removeClass("active");
					$(".stepTabs a.active").removeClass("active");

					$(".stepContentContainer > div:last").addClass("active");
					$(".stepTabs a:last").addClass("active");
				}
				window.slimmage.cr();
				return false;
			});
		}
	}

	initProcessTabs() {
		if (document.querySelectorAll('.stepTabs').length) {

			const tabContent = document.querySelectorAll('.stepContent');
			const tabs = document.querySelectorAll('.stepTabs a')

			tabs.forEach(tab => {
				tab.addEventListener('click', () => {
					var tabIndex = tab.dataset.tab

					const step = document.querySelector('.stepContent[data-step="' + tabIndex + '"]')
					const activeTab = document.querySelector('.stepTabs a[data-tab="' + tabIndex + '"]')
				
					if (step.classList.contains('active')) {
						// console.log('is active')
					} else {
						tabContent.forEach(activeContent => {
							activeContent.classList.remove('active');
						})

						tabs.forEach(activeTab => {
							activeTab.classList.remove('active');
						})

						activeTab.classList.add('active');
						step.classList.add('active');
					};

				});
			});
		};

	}

	initProcessFixedHeight() {
		if (document.querySelectorAll('.stepContent').length) {
			
			function fixHeight(elem){
				var maxHeight = 0;

				$(elem).css('height','auto');
				$(elem).each(function(){
				   if ($(this).height() > maxHeight) { maxHeight = $(this).height(); }
				});
				$(elem).height(maxHeight);
			}

			setTimeout(() => {
				fixHeight('.stepContent');
			}, 1000);
			
			$(window).resize(function () {
				fixHeight('.stepContent');
			});
		}
	}

	initTreatmentteamPopup() {
		if (document.querySelectorAll('.specialistPopupContainer').length) {
			document.querySelectorAll('.closePopup').forEach(close => {
				close.addEventListener('click', () => {
					let popupContainer = document.querySelector('.specialistInfo.active');
					popupContainer.classList.remove('active');
					document.documentElement.classList.remove('disableScroll');
				});
			});

			document.addEventListener('keydown', e => {
				if (e.key.toLowerCase().substr(0, 3) == 'esc') {
					let popupContainer = document.querySelector('.specialistInfo.active');
					if (popupContainer) {
						popupContainer.classList.remove('active');
						document.documentElement.classList.remove('disableScroll');
					}
				}
			});

			document.querySelectorAll('.showPopup').forEach(show => {
				show.addEventListener('click', () => {
					let specialistIndex = show.dataset.link;
					let specialistPopup = document.querySelector('.specialistInfo[data-specialist="' + specialistIndex + '"]');

					specialistPopup.classList.add('active');
					window.slimmage.cr();
					document.documentElement.classList.add('disableScroll');
				});
			});
		};
	}

	insertTesters() {
		this.insertBreakpointIndicator();
		this.insertColumnTester();
	}

	insertColumnTester() {
		let tester = document.createElement('div');
		tester.classList.add('columnTester');
		let cols = ``;
		for (let i=0;i<12;i++) {
			cols += `<div class="col-1"><div></div></div>`;
		}
		tester.innerHTML = `<div class="container"><div class="row">${cols}</div></div>`;
		document.body.appendChild(tester);
	}

	insertBreakpointIndicator() {
		const breakpoints = Object.keys(this.breakpoints);
		let blocks = ``;
		breakpoints.forEach((breakpoint, index) => {
			let classes = 'col ';
			if (index == 0) {
				classes += `d-${breakpoints[index + 1]}-none`;
			} else if (index == breakpoints.length - 1) {
				classes += `d-none d-${breakpoint}-block`;
			} else {
				classes += `d-none d-${breakpoint}-block d-${breakpoints[index + 1]}-none`;
			}
			blocks += `<div class="${classes}"><div class="breakpointIndicator">${breakpoint}</div></div>`;
		});

		
		let tester = document.createElement('div');
		tester.classList.add('breakpointIndicators');

		tester.innerHTML = `<div class="container"><div class="row">${blocks}</div></div>`;
		document.body.appendChild(tester);
	}
}